import { forwardRef } from "react";
import dayjs from "dayjs";
import { TClient } from "~/types";
import { getClientName } from "~/utils";

interface InvoiceLineItem {
  description: string;
  quantity: number;
  rate: number;
  amount: number;
}

interface StorageInvoiceData {
  invoiceNumber: string;
  invoiceDate: string;
  billingPeriod: string;
  client: TClient;
  lineItems: InvoiceLineItem[];
  subtotal: number;
  taxRate: number;
  taxAmount: number;
  total: number;
  notes: string;
}

interface StorageInvoicePrintContentProps {
  invoiceData: StorageInvoiceData;
}

const formatCurrency = (amount: number): string => {
  return `$${Number(amount ?? 0)
    .toFixed(2)
    .replace(/\d(?=(\d{3})+\.)/g, "$&,")}`;
};

export const StorageInvoicePrintContent = forwardRef<
  HTMLDivElement,
  StorageInvoicePrintContentProps
>(({ invoiceData }, componentRef) => {
  const { invoiceNumber, invoiceDate, billingPeriod, client, lineItems, total, notes } =
    invoiceData;

  const clientAddress = client.shippingAddresses?.[0] || client.billingAddress;

  return (
    <>
      <div className="printable-storage-invoice-content">
        <div ref={componentRef} className="storage-invoice-container">
          {/* Invoice Header */}
          <div className="storage-invoice-header">
            <div className="company-info">
              <h1>ART HANDLER COLLECTIVE</h1>
              <p>15 Cumberland Street</p>
              <p>Brooklyn, NY 11206</p>
              <p>Phone: (646) 741-4047</p>
              <p>Email: info@arthandlercollective.org</p>
            </div>
            <div className="invoice-meta">
              <h2>STORAGE INVOICE</h2>
              <p>
                <strong>Invoice #:</strong> {invoiceNumber}
              </p>
              <p>
                <strong>Date:</strong> {invoiceDate}
              </p>
              <p>
                <strong>Billing Period:</strong> {billingPeriod}
              </p>
            </div>
          </div>

          {/* Bill To Section */}
          <div className="bill-to-section">
            <h3>BILL TO:</h3>
            <div className="client-details">
              <p className="client-name">
                {client.companyName || getClientName(client)}
              </p>
              {clientAddress && (
                <>
                  <p>{clientAddress.address}</p>
                  {clientAddress.address2 && <p>{clientAddress.address2}</p>}
                  <p>
                    {clientAddress.city}, {clientAddress.state}{" "}
                    {clientAddress.zip}
                  </p>
                </>
              )}
              {client.email && <p>Email: {client.email}</p>}
              {client.contactPhone && <p>Phone: {client.contactPhone}</p>}
            </div>
          </div>

          {/* Line Items */}
          <div className="line-items-section">
            <table className="line-items-table">
              <thead>
                <tr>
                  <th>Description</th>
                  <th className="text-right">Amount</th>
                </tr>
              </thead>
              <tbody>
                {lineItems.map((item, index) => (
                  <tr key={index}>
                    <td>{item.description}</td>
                    <td className="text-right amount-cell">
                      {formatCurrency(item.amount)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Totals Section */}
          <div className="totals-section">
            <div className="totals-grid">
              <div className="total-row grand-total-row">
                <span className="total-label">Total Due:</span>
                <span className="total-amount grand-total-amount">
                  {formatCurrency(total)}
                </span>
              </div>
            </div>
          </div>

          {/* Payment Terms */}
          <div className="payment-terms-section">
            <h3>PAYMENT TERMS</h3>
            <p>
              <strong>Due Upon Receipt</strong>
            </p>
            {notes && <p className="notes-text">{notes}</p>}
          </div>

          {/* Footer */}
          <div className="invoice-footer">
            <p>Thank you for storing with Art Handler Collective!</p>
            <p className="footer-note">
              Please include invoice number {invoiceNumber} with your payment.
            </p>
            <p className="footer-note">
              For questions regarding this invoice, please contact us at
              info@arthandlercollective.org
            </p>
          </div>
        </div>
      </div>

      <style>
        {`
          .printable-storage-invoice-content {
            position: absolute;
            opacity: 0;
            height: 0;
            overflow: hidden;
          }

          .storage-invoice-container {
            width: 816px;
            font-family: 'Helvetica', 'Arial', sans-serif;
            font-size: 11px;
            line-height: 1.4;
            color: #333;
            background: white;
            padding: 40px;
            box-sizing: border-box;
          }

          .storage-invoice-header {
            display: flex;
            justify-content: space-between;
            margin-bottom: 30px;
            padding-bottom: 20px;
            border-bottom: 3px solid #333;
          }

          .storage-invoice-header .company-info h1 {
            font-size: 24px;
            font-weight: bold;
            margin: 0 0 10px 0;
            color: #000;
          }

          .storage-invoice-header .company-info p {
            margin: 2px 0;
            font-size: 10px;
          }

          .storage-invoice-header .invoice-meta {
            text-align: right;
          }

          .storage-invoice-header .invoice-meta h2 {
            font-size: 24px;
            font-weight: bold;
            margin: 0 0 10px 0;
            color: #000;
          }

          .storage-invoice-header .invoice-meta p {
            margin: 4px 0;
            font-size: 11px;
          }

          .storage-invoice-container .bill-to-section {
            margin-bottom: 30px;
          }

          .storage-invoice-container .bill-to-section h3 {
            font-size: 12px;
            font-weight: bold;
            margin: 0 0 10px 0;
            color: #666;
            letter-spacing: 0.5px;
          }

          .storage-invoice-container .client-details {
            background: #f8f8f8;
            padding: 15px;
            border-left: 3px solid #333;
          }

          .storage-invoice-container .client-name {
            font-size: 13px;
            font-weight: bold;
            margin-bottom: 8px;
          }

          .storage-invoice-container .client-details p {
            margin: 3px 0;
            font-size: 10px;
          }

          .storage-invoice-container .line-items-section {
            margin-bottom: 30px;
          }

          .storage-invoice-container .line-items-table {
            width: 100%;
            border-collapse: collapse;
            margin-top: 10px;
          }

          .storage-invoice-container .line-items-table thead {
            background: #333;
            color: white;
          }

          .storage-invoice-container .line-items-table th {
            padding: 10px 8px;
            text-align: left;
            font-size: 10px;
            font-weight: bold;
            text-transform: uppercase;
            letter-spacing: 0.5px;
          }

          .storage-invoice-container .line-items-table th.text-right {
            text-align: right;
          }

          .storage-invoice-container .line-items-table td {
            padding: 12px 8px;
            border-bottom: 1px solid #ddd;
            vertical-align: top;
            font-size: 11px;
          }

          .storage-invoice-container .line-items-table tbody tr:last-child td {
            border-bottom: 2px solid #333;
          }

          .storage-invoice-container .amount-cell {
            font-weight: bold;
            font-size: 11px;
          }

          .storage-invoice-container .text-right {
            text-align: right;
          }

          .storage-invoice-container .totals-section {
            margin-bottom: 30px;
            display: flex;
            justify-content: flex-end;
          }

          .storage-invoice-container .totals-grid {
            width: 300px;
            background: #f8f8f8;
            padding: 15px 20px;
            border: 1px solid #ddd;
          }

          .storage-invoice-container .total-row {
            display: flex;
            justify-content: space-between;
            padding: 8px 0;
            border-bottom: 1px solid #ddd;
          }

          .storage-invoice-container .total-row:last-child {
            border-bottom: none;
          }

          .storage-invoice-container .total-label {
            font-size: 11px;
            color: #666;
          }

          .storage-invoice-container .total-amount {
            font-size: 11px;
            font-weight: bold;
          }

          .storage-invoice-container .grand-total-row {
            margin-top: 10px;
            padding-top: 15px !important;
            border-top: 2px solid #333 !important;
            border-bottom: none;
          }

          .storage-invoice-container .grand-total-row .total-label {
            font-size: 13px;
            font-weight: bold;
            color: #000;
          }

          .storage-invoice-container .grand-total-amount {
            font-size: 16px;
            font-weight: bold;
            color: #000;
          }

          .storage-invoice-container .payment-terms-section {
            margin-bottom: 30px;
            padding: 15px;
            background: #f8f8f8;
            border: 1px solid #ddd;
          }

          .storage-invoice-container .payment-terms-section h3 {
            font-size: 12px;
            font-weight: bold;
            margin: 0 0 10px 0;
            color: #666;
            letter-spacing: 0.5px;
          }

          .storage-invoice-container .payment-terms-section p {
            margin: 6px 0;
            font-size: 10px;
          }

          .storage-invoice-container .notes-text {
            margin-top: 10px;
            font-style: italic;
            color: #666;
          }

          .storage-invoice-container .invoice-footer {
            text-align: center;
            margin-top: 40px;
            padding-top: 20px;
            border-top: 2px solid #ddd;
          }

          .storage-invoice-container .invoice-footer p {
            margin: 6px 0;
            font-size: 11px;
          }

          .storage-invoice-container .footer-note {
            font-size: 9px;
            color: #666;
          }

          @media print {
            @page {
              size: letter;
              margin: 0.75in 0.5in;
            }

            .printable-storage-invoice-content {
              position: static;
              opacity: 1;
              height: auto;
              overflow: visible;
            }

            .storage-invoice-container {
              width: 100%;
              padding: 0 30px;
              box-sizing: border-box;
            }

            .storage-invoice-container .line-items-table {
              page-break-inside: avoid;
            }

            .storage-invoice-container .totals-section {
              page-break-inside: avoid;
            }

            .storage-invoice-container .payment-terms-section {
              page-break-inside: avoid;
            }
          }
        `}
      </style>
    </>
  );
});

StorageInvoicePrintContent.displayName = "StorageInvoicePrintContent";
